import { observer } from "mobx-react-lite"
import { useStores } from "../../../models/helpers/useStores"
import { Room } from "../../molecules/Room"
import styles from "./styles.module.scss"
import PlusCircleOutline from "~icons/eva/plus-circle-outline"
import { Room as IRoom } from "@cruisen/react-shared"
import { fetchAllTripUsersProfiles } from "../../../services/firebase-functions/profile"
import { useQuery } from "@tanstack/react-query"
import classNames from "classnames"
import { prepareTripRoomGuestProfiles } from "../BookingModal/utils"
import { ITripRoomGuest } from "@cruisen/contract"
import PeopleIcon from "~icons/eva/people-outline"
import { Popover } from "../../atoms/Popover"
import { AddRoomForm } from "./AddRoomForm"
import { makeAutoObservable } from "mobx"
import { useMemo, useRef } from "react"
import { useDndScrolling } from "react-dnd-scrolling"
import { TripRoomGuestCard, TripRoomGuestCardItem } from "../../cards/TripRoomGuestCard"
import { useDrop } from "react-dnd"
import { removeGuestFromTripRoom } from "../../../services/firebase-functions/rooms"
import Trash2Outline from "~icons/eva/trash-2-outline"
import { useGuestActions } from "../../../hooks/useGuestActions"

class RoomListStore {
  visibleModal: null | "addRoom"
  recentlyDroppedGuests: string[]

  constructor() {
    this.visibleModal = null
    this.recentlyDroppedGuests = []

    makeAutoObservable(this)
  }

  setModal(modal: null | "addRoom") {
    this.visibleModal = modal
  }

  toggleModal(modal: null | "addRoom") {
    if (this.visibleModal === modal) {
      this.visibleModal = null
      return
    }
    this.visibleModal = modal
  }

  setRecentlyDroppedGuests(recentlyDroppedGuests: string[]) {
    this.recentlyDroppedGuests = recentlyDroppedGuests
  }

  isRecentlyDroppedGuest(guestId: string) {
    return this.recentlyDroppedGuests.includes(guestId)
  }
}

export const RoomList = observer(function RoomList() {
  const rootStore = useStores()
  const rooms = rootStore?.activeTrip?.rooms || []
  const store = useMemo(() => new RoomListStore(), [])
  const { removeGuestFromRoom } = useGuestActions()
  const roomGuestsAmount = rooms.map((room) => room.guests.length)

  const assignedUserIds = useMemo(
    () =>
      rooms.reduce((acc: string[], room) => {
        const userIds = room.guests.map((guest) => guest.userId).filter(Boolean) as string[]
        return [...acc, ...userIds]
      }, []),
    [JSON.stringify(roomGuestsAmount)],
  )

  const unassignedCollaborators = rootStore.activeTrip?.collaborators.reduce(
    (acc, collaborator) => {
      if (!assignedUserIds.includes(collaborator.userId) && collaborator?.type !== "agent") {
        acc.push(collaborator)
      }
      return acc
    },
    [],
  )

  const profilesQuery = useQuery({
    queryKey: ["allProfiles", rootStore.activeTrip?.id],
    queryFn: () => fetchAllTripUsersProfiles(rootStore.activeTrip?.id),
    staleTime: 0,
  })

  const profiles = profilesQuery.data ?? []

  const ref = useRef()
  useDndScrolling(ref)

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "tripRoomGuestCard",
      canDrop: (item: TripRoomGuestCardItem) => {
        return Boolean(item.guest.userId) && assignedUserIds.includes(item.guest.userId)
      },
      drop: async (item: TripRoomGuestCardItem) => {
        await handleDrop(item)
      },
      collect: (monitor) => ({
        isOver: Boolean(monitor.isOver() && monitor.canDrop()),
      }),
    }),
    [assignedUserIds.toString()],
  )

  const handleRecentlyDroppedGuests = (guestId: string) => {
    store.setRecentlyDroppedGuests([...store.recentlyDroppedGuests, guestId])
    setTimeout(() => {
      store.setRecentlyDroppedGuests(store.recentlyDroppedGuests.filter((id) => id !== guestId))
    }, 2000)
  }

  const handleDrop = async (item: TripRoomGuestCardItem) => {
    removeGuestFromRoom(item.guest.id, item.roomId)
    handleRecentlyDroppedGuests(item.guest.userId || item.guest.id)

    await removeGuestFromTripRoom({
      tripId: rootStore.activeTrip!.id,
      roomId: item.roomId,
      guestId: item.guest.id,
    })
  }

  if (!rootStore.activeTrip || !rootStore.loggedInUser) return null

  return (
    <div className={styles.roomListContainer} data-testid="room-list">
      <div className={styles.roomListHeading}>
        <PeopleIcon />
        <h1 className={styles.roomListTitle}>Rooms</h1>
      </div>
      <div className={styles.roomElements} ref={ref}>
        <div className={styles.roomListRooms}>
          {rooms.map((room: IRoom, index) => (
            <Room
              key={room.id}
              roomData={room}
              testID={`room-${index + 1}`}
              showDeleteRoom={rooms.length > 1}
              isRecentlyDroppedGuest={(guestId) => store.isRecentlyDroppedGuest(guestId)}
              setRecentlyDroppedGuests={(guestId) => handleRecentlyDroppedGuests(guestId)}
              tripRoomProfiles={prepareTripRoomGuestProfiles(
                room.guests as ITripRoomGuest[],
                profiles,
                profilesQuery.isLoading,
              )}
            />
          ))}
          {unassignedCollaborators.length > 0 && profiles.length > 0 && (
            <div
              className={styles.unassignedUsersContainer}
              data-testid="unassigned-users-container"
            >
              <div className={styles.unassignedUsersHeader}>
                <p className={styles.unassignedTitle}>Unassigned</p>
                <p className={styles.unassignedDescription}>
                  Inform users to join a room to be included in booking
                </p>
              </div>
              <div className={styles.unassignedUserList}>
                {unassignedCollaborators.map((user, index) => {
                  const profile = profiles.find(
                    (profile) => profile.isPrimaryProfile && profile.ownerUserId === user.userId,
                  )
                  return (
                    <div
                      className={classNames(
                        styles.guestContainer,
                        store.isRecentlyDroppedGuest(user.userId) ? styles.guestAnimation : "",
                      )}
                      key={user.userId}
                    >
                      <TripRoomGuestCard
                        guest={{
                          id: "",
                          firstName: user.firstName,
                          lastName: user.lastName,
                          avatar: user.avatar,
                          email: profile?.email || "",
                          phoneNumber: profile?.phone || "",
                          ageGroup: profile?.ageGroup || "adult",
                          userId: user.userId,
                          profileId: profile?.id,
                        }}
                        testID={`unnassigned-guest-${index + 1}`}
                        isProfileIncomplete={false}
                        activePaymentRequest={false}
                        roomId={""}
                        tripId={rootStore.activeTrip!.id}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
        <div
          className={classNames(
            styles.roomListButtonContainer,
            rooms?.length === 0 && styles.emptyRoomList,
          )}
          ref={drop}
        >
          {isOver && (
            <div className={styles.dropZone}>
              <Trash2Outline className={styles.dropZoneIcon} />
            </div>
          )}
          <Popover
            isOpen={store.visibleModal === "addRoom"}
            onClickOutside={() => store.setModal(null)}
            content={
              <AddRoomForm
                closePopover={() => store.setModal(null)}
                tripId={rootStore.activeTrip!.id}
                defaultName={`Room ${rooms.length + 1}`}
              />
            }
          >
            <button
              className={styles.roomListAddRoomButton}
              data-testid="add-room-button"
              onClick={() => store.toggleModal("addRoom")}
            >
              <PlusCircleOutline className={styles.addRoomIcon} />
              <p>Add room</p>
            </button>
          </Popover>
          {rooms?.length === 0 && (
            <p className={styles.emptyRoomListText}>
              Add your first room to get started on your trip.
            </p>
          )}
        </div>
      </div>
    </div>
  )
})
